import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserPresence, UserStatus } from '@/types/presence';
import { useAuth } from '@/contexts/AuthContext';

// 🔥 RETRY HELPER: Wrapper with automatic session refresh
const withSessionRetry = async <T,>(
  operation: () => Promise<T>,
  maxRetries: number = 1
): Promise<T> => {
  try {
    return await operation();
  } catch (error: any) {
    // If RLS error (42501), attempt session refresh
    if (error?.code === '42501' && maxRetries > 0) {
      console.warn('⚠️ [RLS ERROR] - Attempting session refresh and retry');
      
      const { error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.error('❌ [REFRESH FAILED]', refreshError);
        throw error;
      }
      
      console.log('✅ [SESSION REFRESHED] - Retrying operation');
      return withSessionRetry(operation, maxRetries - 1);
    }
    
    throw error;
  }
};

interface UseUserPresenceReturn {
  userPresences: Map<string, UserPresence>;
  myPresence: UserPresence | null;
  updateMyStatus: (status: UserStatus, message?: string) => Promise<void>;
  updateActivity: () => Promise<void>;
  isLoading: boolean;
}

const ACTIVITY_TIMEOUT_MINUTES = 5;

export const useUserPresence = (): UseUserPresenceReturn => {
  const { user } = useAuth();
  const [userPresences, setUserPresences] = useState<Map<string, UserPresence>>(new Map());
  const [myPresence, setMyPresence] = useState<UserPresence | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMounted = useRef(true);

  // Fetch all user presences - using useMemo for stability
  const fetchPresences = useMemo(() => async () => {
    try {
      const { data, error } = await supabase
        .from('user_presence')
        .select('*');

      if (error) throw error;

      const presenceMap = new Map<string, UserPresence>();
      data?.forEach(presence => {
        const typedPresence = presence as UserPresence;
        presenceMap.set(typedPresence.user_id, typedPresence);
        if (user && typedPresence.user_id === user.id) {
          setMyPresence(typedPresence);
        }
      });

      setUserPresences(presenceMap);
    } catch (error) {
      console.error('Error fetching user presences:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Initialize presence for current user - using useMemo for stability
  const initializePresence = useMemo(() => async () => {
    if (!user) return;

    try {
      console.log('[initializePresence] Initializing presence for user:', user.id);
      
      // Check if presence already exists
      const { data: existingPresence, error: selectError } = await supabase
        .from('user_presence')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      console.log('[initializePresence] Existing presence:', existingPresence, 'Error:', selectError);

      if (existingPresence) {
        // Update existing presence to active - DO NOT modify status
        console.log('[initializePresence] Updating activity without changing status');
        
        const { error: updateError } = await supabase
          .from('user_presence')
          .update({
            is_active: true,
            last_seen: new Date().toISOString(),
            activity_timeout: new Date(Date.now() + ACTIVITY_TIMEOUT_MINUTES * 60 * 1000).toISOString()
          })
          .eq('user_id', user.id);
          
        console.log('[initializePresence] Update complete. Error:', updateError);
      } else {
        // Create new presence
        console.log('[initializePresence] Creating new presence with status: available');
        
        const { data: insertedPresence, error: insertError } = await supabase
          .from('user_presence')
          .insert({
            user_id: user.id,
            status: 'available',
            is_active: true,
            last_seen: new Date().toISOString(),
            activity_timeout: new Date(Date.now() + ACTIVITY_TIMEOUT_MINUTES * 60 * 1000).toISOString()
          })
          .select()
          .single();
          
        console.log('[initializePresence] Insert complete. Error:', insertError);
        
        if (!insertError && insertedPresence) {
          setMyPresence(insertedPresence as UserPresence);
        }
      }
    } catch (error) {
      console.error('[initializePresence] Error initializing presence:', error);
    }
  }, [user]);

  // Update user status - using useMemo for stability
  const updateMyStatus = useMemo(() => async (status: UserStatus, message?: string) => {
    if (!user) return;

    // 🔥 SESSION GUARD: Verify valid session before operation
    return withSessionRetry(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session || !session.access_token) {
        console.error('❌ [NO VALID SESSION] - Cannot update presence');
        throw new Error('Session invalide - veuillez vous reconnecter');
      }
      
      console.log('✅ [VALID SESSION]', {
        userId: session.user.id,
        expiresAt: new Date(session.expires_at! * 1000).toISOString()
      });

      // Check if presence record exists
      const { data: existingPresence } = await supabase
        .from('user_presence')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      const presenceData = {
        user_id: user.id,
        status,
        status_message: message || null,
        is_active: status !== 'offline',
        last_seen: new Date().toISOString(),
        activity_timeout: status !== 'offline' 
          ? new Date(Date.now() + ACTIVITY_TIMEOUT_MINUTES * 60 * 1000).toISOString()
          : null
      };

      let error;
      
      if (existingPresence) {
        // UPDATE existing record
        const result = await supabase
          .from('user_presence')
          .update(presenceData)
          .eq('user_id', user.id);
        error = result.error;
      } else {
        // INSERT new record
        const result = await supabase
          .from('user_presence')
          .insert(presenceData);
        error = result.error;
      }

      if (error) {
        console.error('Error updating status:', error);
        throw error;
      }

      // Update local state
      setMyPresence(prev => prev ? { ...prev, status, status_message: message } : null);
    });
  }, [user]);

  // Update activity (heartbeat) - DO NOT modify status
  const updateActivity = useMemo(() => async () => {
    if (!user) return;

    try {
      console.log('[updateActivity] Updating activity without changing status');

      const { error: updateError } = await supabase
        .from('user_presence')
        .update({
          last_seen: new Date().toISOString(),
          activity_timeout: new Date(Date.now() + ACTIVITY_TIMEOUT_MINUTES * 60 * 1000).toISOString(),
          is_active: true
        })
        .eq('user_id', user.id);

      console.log('[updateActivity] Update complete. Error:', updateError);
    } catch (error) {
      console.error('[updateActivity] Error updating activity:', error);
    }
  }, [user]);

  // 🔥 PHASE 1: Polling 2 min + presenceOptimizer au lieu de Realtime
  useEffect(() => {
    if (!user) return;

    import('@/utils/realtimeOptimizer').then(({ createPollingInterval }) => {
      const stopPolling = createPollingInterval(
        'user_presence',
        fetchPresences,
        true
      );

      return () => {
        stopPolling();
      };
    });
  }, [user, fetchPresences]);

  // Initialize on mount - only re-run when user?.id changes
  useEffect(() => {
    if (user) {
      const initializeAndFetch = async () => {
        await initializePresence();
        await fetchPresences();
      };
      initializeAndFetch();
    }
  }, [user?.id, initializePresence, fetchPresences]);

  // Activity tracking with mouse movement and clicks
  useEffect(() => {
    if (!user) return;

    let activityTimer: NodeJS.Timeout;

    const handleActivity = () => {
      clearTimeout(activityTimer);
      activityTimer = setTimeout(() => {
        updateActivity();
      }, 120000); // Update every 2 minutes when active
    };

    // Listen to user activity
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('click', handleActivity);
    document.addEventListener('keypress', handleActivity);

    // Initial activity update
    handleActivity();

    return () => {
      clearTimeout(activityTimer);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('keypress', handleActivity);
    };
  }, [user, updateActivity]);

  // Cleanup on unmount - Only on true component unmount
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      
      // Only execute on true unmount, not on user object changes
      setTimeout(() => {
        if (!isMounted.current && user) {
          supabase
            .from('user_presence')
            .update({
              status: 'offline',
              is_active: false
            })
            .eq('user_id', user.id);
        }
      }, 100);
    };
  }, []); // Empty array = only runs on mount/unmount

  return {
    userPresences,
    myPresence,
    updateMyStatus,
    updateActivity,
    isLoading
  };
};